import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Flame, ArrowRight, Check } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

export default function ComboBuilderDialog({ open, onOpenChange, pastaFlavours, wingFlavours, comboPrice }) {
  const [step, setStep] = useState(1);
  const [pasta, setPasta] = useState(null);
  const [wing, setWing] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    if (open) { setStep(1); setPasta(null); setWing(null); }
  }, [open]);

  const finish = () => {
    if (!pasta || !wing) return;
    const label = `COMBO: ${pastaFlavours[pasta].name} + 5 ${wingFlavours[wing].name} Wings`;
    addItem({
      is_combo: true,
      combo_pasta: pasta,
      combo_wings: wing,
      name: label,
      unit_price: comboPrice,
      quantity: 1,
    });
    toast.success("Combo added to basket", { description: label });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-black border-2 border-gold rounded-none max-w-2xl p-0 overflow-hidden"
        data-testid="combo-builder-dialog"
      >
        <div className="grunge-noise absolute inset-0 opacity-40 pointer-events-none" />
        <div className="relative">
          <DialogHeader className="px-6 pt-6">
            <div className="flex items-center justify-between">
              <DialogTitle className="font-heading text-cream text-3xl uppercase tracking-tight">
                £{comboPrice.toFixed(2)} Combo Builder
              </DialogTitle>
              <div className="font-heading text-gold uppercase tracking-widest text-sm">Step {step} / 2</div>
            </div>
            <DialogDescription className="text-cream/70 font-body">
              {step === 1 ? "Pick your pasta box." : "Pick your 5 wings flavour."}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-6 mt-4">
            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(pastaFlavours).map(([key, val]) => {
                  const active = pasta === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setPasta(key)}
                      className={`text-left p-4 border-2 transition-all ${active ? "border-gold bg-gold/10" : "border-border hover:border-gold/60"}`}
                      data-testid={`combo-pasta-${key}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-heading text-cream uppercase tracking-tight text-2xl leading-none">
                            {val.name}
                          </div>
                          <div className="text-cream/70 text-sm mt-1">{val.desc}</div>
                        </div>
                        {active && <Check className="h-5 w-5 text-gold" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 2 && (
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(wingFlavours).map(([key, val]) => {
                  const active = wing === key;
                  const hot = key === "xstreamely_hot";
                  return (
                    <button
                      key={key}
                      onClick={() => setWing(key)}
                      className={`text-left p-4 border-2 transition-all ${active ? "border-gold bg-gold/10" : "border-border hover:border-gold/60"}`}
                      data-testid={`combo-wing-${key}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="font-heading text-cream uppercase tracking-tight text-2xl leading-none">
                          {val.name}
                        </div>
                        {hot && <Flame className="h-5 w-5 text-accent" />}
                        {active && !hot && <Check className="h-5 w-5 text-gold" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={() => onOpenChange(false)}
                className="text-cream/60 font-heading uppercase tracking-widest text-sm hover:text-cream"
                data-testid="combo-cancel-btn"
              >
                Cancel
              </button>
              <div className="flex gap-3">
                {step === 2 && (
                  <button
                    onClick={() => setStep(1)}
                    className="btn-outline-poster"
                    data-testid="combo-back-btn"
                  >
                    Back
                  </button>
                )}
                {step === 1 && (
                  <button
                    onClick={() => pasta && setStep(2)}
                    disabled={!pasta}
                    className={`btn-poster ${!pasta ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid="combo-next-btn"
                  >
                    Next <ArrowRight className="h-5 w-5 ml-2" />
                  </button>
                )}
                {step === 2 && (
                  <button
                    onClick={finish}
                    disabled={!wing}
                    className={`btn-poster ${!wing ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid="combo-add-to-basket-btn"
                  >
                    Add to Basket — £{comboPrice.toFixed(2)}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
